import {
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";
import type { ReactNode } from "react";
import { BulkAssignmentConfirmDialog } from "../components/bulkAssignmentConfirmDialog.component";

export interface BulkConfirmDialogData {
	isRemoval: boolean;
	totalSelected: number;
	affectedDetails: string[];
	duplicateDetails: string[];
	applicableCount: number;
	stepNumber: number | null;
}

interface BulkAssignmentDialogContextValue {
	openConfirmDialog: (data: BulkConfirmDialogData) => Promise<boolean>;
}

const BulkAssignmentDialogContext =
	createContext<BulkAssignmentDialogContextValue | null>(null);

/**
 * Provider for the bulk assignment dialog
 * @param children - The children of the provider
 * @returns
 */
export const BulkAssignmentDialogProvider: React.FC<{
	children: ReactNode;
}> = ({ children }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [dialogData, setDialogData] = useState<BulkConfirmDialogData | null>(
		null,
	);
	const resolverRef = useRef<((value: boolean) => void) | null>(null);

	/**
	 * Opens the confirmation dialog
	 * @param data - The data for the dialog
	 * @returns
	 */
	const openConfirmDialog = useCallback(
		(data: BulkConfirmDialogData): Promise<boolean> => {
			setDialogData(data);
			setIsOpen(true);
			return new Promise<boolean>((resolve) => {
				resolverRef.current = resolve;
			});
		},
		[],
	);

	/**
	 * Handles the confirmation of the dialog
	 */
	const handleConfirm = useCallback(() => {
		setIsOpen(false);
		setDialogData(null);
		resolverRef.current?.(true);
		resolverRef.current = null;
	}, []);

	/**
	 * Handles the cancellation of the dialog
	 */
	const handleCancel = useCallback(() => {
		setIsOpen(false);
		setDialogData(null);
		resolverRef.current?.(false);
		resolverRef.current = null;
	}, []);

	return (
		<BulkAssignmentDialogContext.Provider value={{ openConfirmDialog }}>
			{children}
			<BulkAssignmentConfirmDialog
				isOpen={isOpen}
				info={dialogData}
				onCancel={handleCancel}
				onConfirm={handleConfirm}
			/>
		</BulkAssignmentDialogContext.Provider>
	);
};

/**
 * Hook to use the bulk assignment dialog
 * @returns The bulk assignment dialog context
 */
export const useBulkAssignmentConfirmDialog = () => {
	const ctx = useContext(BulkAssignmentDialogContext);
	if (!ctx)
		throw new Error(
			"useBulkAssignmentConfirmDialog must be used within BulkAssignmentDialogProvider",
		);
	return ctx;
};
